/**
 * Which ribbon trail a projectile draws, from its ability data, whoever built the ability
 * (docs/plans/2026-11-02-projectile-timing, the trail race).
 *
 * TOML `vfx.trail` decides (trails spec §5): true / omitted → the default ribbon in the projectile's
 * colour, "ribbon" → the same, "<def_id>" → that registered def, false → none. TomlAbilityFactory applied
 * it when it built an ability, but a projectile ability defined in code (HunterAuto.ts and 38 others)
 * never did, so its `${id}_trail` def existed only when something had asked the TOML resolver for the
 * id before the code version loaded. Network timing decided which came first: hunter arrows had a trail
 * locally and none with 100+ ms of delay. Resolving the trail here, at launch, makes it the same every
 * time, for local and remote projectiles alike.
 */
import { ABILITY_DATA, type AbilityDataDef } from '../../../../shared/content/generated/abilities.gen';
import { ABILITY_SCHOOL_COLORS } from '../../../../shared/constants/AbilitySchoolColors';
import { resolveAbilityArchetype } from '../../../../config/abilities/vfxArchetypes.config';
import { VFXEffectRegistry } from '../../../effects/VFXEffectRegistry';
import { registerTrailVFX } from './AbilityPresets';

/** Default ribbon head width for a data-driven projectile trail. */
export const DATA_TRAIL_WIDTH = 4;

/** A TOML projectile's colour: its `projectile_color`, else its class/school palette, else its school's. */
export function tomlProjectileColor(data: AbilityDataDef): number {
  const hex = data.vfx?.projectileColor;
  if (hex) return parseInt(hex.replace('#', ''), 16);
  const school = data.school || 'physical';
  return resolveAbilityArchetype(data.class, data.school || '')?.palette?.primary
    ?? ABILITY_SCHOOL_COLORS[school] ?? 0xcccccc;
}

/**
 * The trail def id a projectile of `abilityId` draws, or undefined for none. A registered
 * `${abilityId}_trail` wins (an authored `.withTrail()` or hand-made def); otherwise the ability data
 * decides, registering the default ribbon on first use.
 */
export function abilityDataTrailId(abilityId: string): string | undefined {
  const conventional = `${abilityId}_trail`;
  if (VFXEffectRegistry.has(conventional)) return conventional;
  const data = (ABILITY_DATA as Record<string, AbilityDataDef | undefined>)[abilityId];
  if (!data) return undefined;
  const trail = data.vfx?.trail;
  if (trail === false) return undefined;
  if (typeof trail === 'string' && trail !== 'ribbon') return trail;
  return registerTrailVFX(abilityId, tomlProjectileColor(data), DATA_TRAIL_WIDTH);
}
